import type { BriefQueue, BriefSinceLast } from '../../data/types';

interface BriefHeroProps {
  queue: BriefQueue;
  sinceLast: BriefSinceLast;
  clusterCount?: number;
}

export function BriefHero({ queue, sinceLast, clusterCount }: BriefHeroProps) {
  const headlineCount = queue.total;
  const severeCount = queue.critical + queue.high;
  const clusters = clusterCount ?? 0;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[1.5fr_1fr] gap-3.5 mb-3.5">
      {/* Hero card */}
      <div className="bg-brand-navy text-white rounded-xl px-6 py-[22px] flex flex-col justify-between min-h-[200px] relative overflow-hidden">
        <div className="flex items-center gap-2 text-[10px] tracking-[0.22em] uppercase font-bold font-sans text-white/55">
          <span className="relative inline-block w-[7px] h-[7px] rounded-full bg-brand-lime shadow-[0_0_0_4px_rgba(185,255,77,0.2)]" />
          State of queue · action needed
        </div>
        <h2 className="font-sans font-medium tracking-[-0.018em] leading-[1.1] my-3 max-w-[32ch] text-white text-[30px]">
          <span className="text-brand-lime">{headlineCount}</span> {headlineCount === 1 ? 'incident needs' : 'incidents need'} a human today.{' '}
          {severeCount > 0 ? (
            <span className="text-white/55 font-normal">
              {severeCount} {severeCount === 1 ? 'is' : 'are'} critical or high severity.
            </span>
          ) : clusters > 0 ? (
            <span className="text-white/55 font-normal">
              Triage has clustered them into {clusters} {clusters === 1 ? 'theme' : 'themes'}.
            </span>
          ) : null}
        </h2>
        <div className="flex flex-wrap gap-x-8 gap-y-3 mt-auto pt-3">
          <Stat k="Assigned to you" v={queue.assignedToMe} />
          <Stat k="Unassigned" v={queue.unassigned} />
          <Stat k="SLA breached" v={queue.slaBreached} accent={queue.slaBreached > 0 ? 'lime' : 'default'} />
          <Stat k="Stale > 24h" v={queue.staleOver24h} />
        </div>
      </div>

      {/* Side KPIs */}
      <div className="grid grid-rows-2 gap-3.5">
        <KpiCard
          label="Critical / High open"
          value={`${queue.critical}/${queue.high}`}
          delta={`${sinceLast.newIncidents} new since last sign-out`}
        />
        <KpiCard
          label="Total open"
          value={queue.total}
          delta={`${sinceLast.reopened} reopened, ${sinceLast.closedByOthers} closed by others`}
        />
      </div>
    </div>
  );
}

function Stat({ k, v, accent = 'default' }: { k: string; v: string | number; accent?: 'default' | 'lime' }) {
  return (
    <div>
      <div className="text-[10px] tracking-[0.18em] uppercase font-bold font-sans text-white/50">{k}</div>
      <div
        className={
          'font-sans font-medium text-[22px] mt-1 ' + (accent === 'lime' ? 'text-brand-lime' : 'text-white')
        }
      >
        {v}
      </div>
    </div>
  );
}

function KpiCard({ label, value, delta }: { label: string; value: string | number; delta: string }) {
  return (
    <div className="bg-bg-secondary border border-border-default rounded-xl px-[18px] py-4 flex flex-col justify-center">
      <div className="text-[10px] tracking-[0.18em] uppercase font-bold font-sans text-text-secondary">{label}</div>
      <div className="font-sans font-medium text-[30px] tracking-[-0.02em] leading-none mt-2 text-text-primary">{value}</div>
      <div className="text-[11.5px] text-text-secondary font-sans mt-2">{delta}</div>
    </div>
  );
}
